/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Heading, Stack, Text, Divider } from '@towersystems/towersystems-design-system';
import page from 'page';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Main from 'components/main';
import { 
	requestConnection, 
	saveConnection, 
	deleteConnection, 
	importConnection, 
	migrateConnection } from 'state/connections/actions';
import Navigation from 'modules/dashboard/connections/navigation';
import Button from 'components/button';
import ButtonGroup from 'components/button-group';
import AnnotatedSection from 'components/annotated-section';
import Card from 'components/card';
import { requestConnectionTypes } from 'state/connection-types/actions';
import LoadingPlaceholder from 'modules/dashboard/connections/loading-placeholder';
import cecSdk from 'lib/customer-ec-sdk';

// input
import SelectResources from './select-resources';
import EditApiConfig from './edit-api-config';
import FormToggle from 'components/forms/form-toggle';
import EditDetails from './edit-details';
import { successNotice } from 'state/notices/actions';
import { getConnection } from 'state/connections/selectors';
import TestApiConnectionButton from './test-api-connection-button';
import TestWebhookOrderCreationButton from './test-webhook-order-creation-button';
import ExportConnectionButton from './export-connection-button';
import JumpingDots from 'components/jumping-dots';

const EditConnection = ({ connectionId, ...props }) => {
	const [saving, setSaving] = useState(false);
	const [requesting, setRequesting] = useState(true);
	const [connection, setConnection] = useState();
	const [values, setValues] = useState({});
	const [weblinkGroups, setWeblinkGroups] = useState(null);
	const [importing, setImporting] = useState(false);
	const [importConnection, setImportConnection] = useState(null);
	const [migrating, setMigrating] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		loadData(connectionId);
	}, []);

	const loadData = async (connectionId) => {
		try {
			let c = await props.requestConnection(connectionId);
			await props.requestConnectionTypes();

			// initialize the form values
			setValues({
				connection_details: c.connection_details,
				config: c.config,
			});

			setImportConnection({
				connection_id: connectionId,
			});

			let weblinkGroups = await cecSdk.weblinkGroup().getWeblinkGroups();
			setWeblinkGroups(weblinkGroups);

			setConnection(c);
			setRequesting(false);
		} catch (error) {
			page('/');
		}
	};

	const handleOnSave = async () => {
		try {
			setSaving(true);
			let c = await props.saveConnection(connectionId, values);
			c && setConnection(c);
			props.successNotice('Saved');
		} catch (error) {}
		setSaving(false);
	};

	const handleOnMigrate = async () => {
		try {
			setMigrating(true);
			//let c = await props.migrateConnection(connectionId, values);
			//c && setConnection(c);
			let response = await cecSdk.connection(connectionId).migrate();
			if(response.status == 200) {
				window.location = '/connections/' + response.response.id;
			}
			props.successNotice('Migrated');

		} catch (error) {}
		setMigrating(false);
	};

	const handleOnImport = async () => {
		try {
			setImporting(true);
			let c = await props.importConnection(connectionId, importConnection);
			c && setConnection(c);
			props.successNotice('Imported!');
			window.location.reload(false);
		} catch (error) {}
		setImporting(false);
	};

	const handleDelete = async () => {
		setShowConfirm(true); // Show the confirmation dialog
	};

	const handleConfirmDelete = async () => {
		try {
			setDeleting(true);
			await props.deleteConnection(connectionId);
			return page('/');
		} catch (error) {}
		setDeleting(false);
	};

	const handleCancelDelete  = async () => {
		setShowConfirm(false); // Close the dialog
	};

	const connection_type = get(connection, ['connection_type', 'code']);

	// Basic example of a ConfirmDialog component (can be more complex with styling, etc.)
	function ConfirmDialog({ message, notes, onConfirm, onCancel }) {
		return (
			<Box className="card__section show-confirm">	
				<Text className="heading">{message}</Text>
				{notes && <Text className="notes">{notes}</Text>}
				<Button className='button-connection__delete btn' onClick={onConfirm}>{deleting ? (
					<div>
						Deleting <JumpingDots />
					</div>
					) : (
						'Confirm'
					)}</Button>
				<Button className='button-connection__delete--cancel btn' onClick={onCancel}>Cancel</Button>
			</Box>
		);
	}

	if (true === requesting) {
		return <LoadingPlaceholder connectionId={connectionId} section='home' />;
	}

	return (
		<Main>
			<Navigation connectionId={connectionId} section='home' />

			<Box paddingTop='xxlarge' paddingBottom='xxlarge'>
				<Box display='flex' justifyContent='spaceBetween' alignItems='center' paddingBottom='xlarge'>
					<Heading level='1' className='main__heading'>
						Setup{' '}
						{connection_type
							.split('_')
							.join(' ')
							.charAt(0) +
							connection_type
								.split('_')
								.join(' ')
								.slice(1)}{' '}
						Link
					</Heading>
					<ButtonGroup>
					    {connection_type == 'shopify' ? (<Button disabled={migrating} onClick={handleOnMigrate}>
							{migrating ? (
								<div>
									Migrating <JumpingDots />
								</div>
							) : (
								'Migrate to GraphQL'
							)}
						</Button>) : null}
						<Button disabled={saving} onClick={handleOnSave}>
							{saving ? (
								<div>
									Saving <JumpingDots />
								</div>
							) : (
								'Save'
							)}
						</Button>
					</ButtonGroup>
				</Box>
				<Divider />
			</Box>

			<Stack space='medium'>
				<Stack space='xlarge' dividers>
					<AnnotatedSection>
						<AnnotatedSection.Annotation
							title='Details'
							description='Enter details to identify your connection.'
						/>
						<AnnotatedSection.Content>
							<Card>
								<EditDetails
									connection={connection}
									weblinkGroups={weblinkGroups}
									defaultValues={{
										name: get(connection, 'name', ''),
										enabled: get(connection, 'enabled', false),
										group_code: get(connection, 'group_code', ''),
									}}
									onChange={(newValues) => {
										setValues({
											...values,
											...newValues,
										});
									}}
								/>
							</Card>
						</AnnotatedSection.Content>
					</AnnotatedSection>
					<AnnotatedSection>
						<AnnotatedSection.Annotation
							title='Api Config'
							description='Enter your api details to allow us to communicate with your eCommerce platform.'
						/>
						<AnnotatedSection.Content>
							<Stack space='large'>
								<Card>
									<Card.Section>
										<EditApiConfig
											connection={connection}
											defaultValues={connection.connection_details}
											onChange={(newValues) => {
												setValues({
													...values,
													connection_details: newValues,
												});
											}}
										/>
									</Card.Section>
								</Card>
								<Card>
									<Card.Section>
										<Box display='flex' alignItems='center' justifyContent='spaceBetween'>
											<Text>Test API connection</Text>
											<TestApiConnectionButton
												connectionId={connectionId}
												details={get(values, 'connection_details')}
											/>
										</Box>
									</Card.Section>
								</Card>
								{connection_type == 'shopify' || connection_type == 'shopify_graphql' ? (
									<Card>
										<Card.Section>
											<Box display='flex' alignItems='center' justifyContent='spaceBetween'>
												<Text>Export Shopify Tags, Weights and Images</Text>
												<ExportConnectionButton
													connectionId={connectionId}
													details={get(values, 'connection_details')}
												/>
											</Box>
										</Card.Section>
									</Card>
								) : null}
							</Stack>
						</AnnotatedSection.Content>
					</AnnotatedSection>
					<AnnotatedSection>
						<AnnotatedSection.Annotation
							title='Resources'
							description='Select which resources will be sent to your eCommerce platform.'
						/>
						<AnnotatedSection.Content>
							<Stack space='large'>
								<Card>
									<Card.Header heading='Select from available resources.' />
									<Card.Section>
										<SelectResources
											connection={connection}
											defaultValues={get(connection, 'config.resources', [])}
											onChange={(newValues) => {
												setValues({
													...values,
													config: {
														...values.config,
														resources: newValues,
													},
												});
											}}
										/>
									</Card.Section>
								</Card>
								{/*<Card>
                                    <Card.Section>
                                        <Text size='xsmall'>
                                            *Deleting resources is not recomended. Use at your own risk.
                                        </Text>
                                        <Text size='xsmall'>*Not all resource types can be deleted.</Text>
                                        <Box
                                            marginTop='large'
                                            alignItems='center'
                                            display='flex'
                                            justifyContent='spaceBetween'
                                        >
                                            <Text>Delete Resources</Text>
                                            <FormToggle
                                                onChange={(deleteResources) => {
                                                    setValues({
                                                        ...values,
                                                        config: { ...values.config, deleteResources },
                                                    });
                                                }}
                                                checked={get(values, 'config.deleteResources', false)}
                                            />
                                        </Box>
                                    </Card.Section>
                                </Card>
                            */}
							</Stack>
						</AnnotatedSection.Content>
					</AnnotatedSection>

					<AnnotatedSection>
						<AnnotatedSection.Annotation
							title='Delete Connection'
							description='Deleted connections will be permanently removed. This action cannot be reversed.'
						/>
						<AnnotatedSection.Content>
							<Card>
								<Card.Section>
									<Button onClick={handleDelete} className='button-connection__delete'>
										Delete connection
									</Button>
									{showConfirm && (
										<ConfirmDialog
											message="Are you sure you want to delete this connection?"
											notes="Deleted connections will be permanently removed. This action cannot be reversed."
											onConfirm={handleConfirmDelete}
											onCancel={handleCancelDelete}
										/>
									)}
								</Card.Section>
							</Card>
						</AnnotatedSection.Content>
					</AnnotatedSection>
				</Stack>
			</Stack>
		</Main>
	);
};

export default connect((state, ownProps) => ({}), {
	requestConnection,
	requestConnectionTypes,
	saveConnection,
	deleteConnection,
	importConnection,
	migrateConnection,
	successNotice,
})(EditConnection);
